import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {TablePagination, Icon, useTheme} from "@material-ui/core";

import { useTripLegs } from "../../../services/trips";
import TripMessaging from "./TripMessaging";

import {ColumnWidth} from "../../../blocks/ColumnHelper";
import AircraftFuelLogDialog from "../../Aircraft/components/AircraftFuelLogDialog";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, MaskedNumber } from '../../../controls';
import MovementsTooltip from './TripListDialogs/MovementsTooltip';

const FinanceTripLegList = (_props) => {
  const queryClient = useQueryClient();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();

  const theme = useTheme();

  const props = {
    ..._props
  }

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const handleFuelLogDialogChange = (event) => {
    if (event.state === "cancel") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
    if (event.state === "save") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
  }

  const handleShowFuelLog = (e, rowData) => {
    setAircraftFuelLogDialogProps({
      ...aircraftFuelLogDialogProps,
      open: true,
      legRecord: rowData,
      onChange: handleFuelLogDialogChange,
      canEdit: state.canEdit
    })
  }

  const extraKeys = [props.tripUuid];

  const tripLegs = useTripLegs({tableRef, queryClient, extraKeys, params: {financeView:"true"}});


  const [aircraftFuelLogDialogProps, setAircraftFuelLogDialogProps] = useState({
    open: false,
    onChange: handleFuelLogDialogChange
  });

  const transportTypes = ['PRIMARY', 'ONWARD', 'TRUCK'];

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: "never",
      emptyValue: "",
      initialEditValue: "",
    };

    return [
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        cellStyle: { padding: "8px 2px 8px 2px" },
        headerStyle: { padding: "8px 2px 8px 2px" },
        title: "Aircraft",
        field: "aircraftId",
        width: 70,
        maxWidth: 70,
        minWidth: 70,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,undefined,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        editable: state.canEdit ? "onAdd" : "never",
        width: 60,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: state.canEdit?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 10px 8px 8px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 10px 8px 12px" },
        headerStyle: state.canEdit?{padding: "8px 2px" }:{padding: "8px 12px" },
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,undefined,undefined,false),
        title: "Dest",
        field: "destinationAirportCode",
        width: 60,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: state.canEdit?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 2px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 12px" },
        headerStyle: state.canEdit?{padding: "8px 2px" }:{padding: "8px 12px" },
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,2,undefined,undefined,undefined,undefined,false),
        title: "Transport Type",
        field: "transportType",
        editable: "never",
        initialEditValue: transportTypes[0],
        render: (rowData) => (
          <ComboSelect
            id="transportType"
            group="TRANSPORT TYPES"
            subGroup=""
            fullWidth
            value={`${rowData.transportType}`}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Flight No",
        field: "flightNo",
        initialEditValue: "",
        render: (record) => (
          <div>
            {record.airlineCode}&nbsp;{record.flightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Departure Time",
        field: "scheduledDepartureTimeUTC",
        render: (rowData) => (
          <div>{moment(rowData.scheduledDepartureTimeUTC).format('DD-MMM HH:mm')}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Arrival Time",
        field: "scheduledArrivalTimeUTC",
        render: (rowData) => {
          return (
          <div>{moment(rowData.scheduledArrivalTimeUTC).format('DD-MMM HH:mm')}</div>
          )
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, 2, undefined,undefined,undefined,undefined,false),
        title: "Times",
        field: "scheduledDepartureTimeUTC",
        editable: "never",
        render: (rowData) => {
          if (rowData.aircraftMovementUuid) {
            return (
              <MovementsTooltip aircraftMovementUuid={rowData.aircraftMovementUuid} tripLeg={rowData} showContracted={true} showScheduled={true}>
                <Icon>access_time_filled</Icon>
              </MovementsTooltip>
            )
          } else {
            return (
              <MovementsTooltip aircraftMovementUuid={rowData.aircraftMovementUuid} tripLeg={rowData} showContracted={true} showScheduled={true}>
                <Icon>access_time</Icon>
              </MovementsTooltip>
            )
          }
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(60),
        title: "Block Hours",
        field: "blockHours",
        editable: "never",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={2}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.blockHours}
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Fuel Price",
        field: "pricePerUsg",
        editable: "never",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={5}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.pricePerUsg}
              prefix="$ "
              allowNegative={false}
            />
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Fuel Uplift",
        field: "upliftFuelUsg",
        editable: "never",
        render: (rowData) => (
          <div style={{maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis"}}>
            <MaskedNumber
              displayType={"text"}
              decimalScale={0}
              fixedDecimalScale={true}
              style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
              value={rowData.upliftFuelUsg}
              suffix=" usg"
              allowNegative={false}
            />
          </div>
        )
      },
    ];
  };

  return (isAuthenticated &&
      <div>
        <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: "2px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Trip Sectors"
            columns={getColumns({...props, canEdit: state.canEdit})}
            actions={[
              {
                icon: 'local_gas_station',
                tooltip: 'Display Fuel Log',
                iconProps: { style: { color: theme.palette.primary.main }},
                onClick: handleShowFuelLog
              }
            ]}
            data={tripLegs.data}
        />
        <AircraftFuelLogDialog
          {...aircraftFuelLogDialogProps}
        />
      </div>
  );
}

export default FinanceTripLegList;
